import type { LevelDef } from '@tessera/engine';
import { WORLD_1_LEVELS } from './world1';
import { WORLD_2_LEVELS } from './world2';
import { WORLD_3_LEVELS } from './world3';
import { WORLD_4_LEVELS } from './world4';
import { WORLD_5_LEVELS } from './world5';
import { WORLD_6_LEVELS } from './world6';
import { WORLD_7_LEVELS } from './world7';
import { WORLD_8_LEVELS } from './world8';
import { WORLD_9_LEVELS } from './world9';
import { WORLD_10_LEVELS } from './world10';
// Drip-release queue — DRIPPED 2026-04-30 ALL FOUR AT ONCE for the Android
// Closed Testing cohort (45+ testers). Tester-mode (TEST_MODE_UNLOCK_ALL =
// true in App.tsx) is on for this build, so testers can play any level in
// any world without progression gating — designed for bug-hunting, not
// production gameplay. When iOS production launches, both flags revert:
// TEST_MODE_UNLOCK_ALL → false, and these imports/entries stay (the worlds
// are real content; just the gating flips back to free-W1-W3-sequential
// + paid-full-unlock per Apple Guideline 2.1(b)).
// See docs/future/drip-release-playbook.md.
import { WORLD_11_LEVELS } from './world11';
import { WORLD_12_LEVELS } from './world12';
import { WORLD_13_LEVELS } from './world13';
import { WORLD_14_LEVELS } from './world14';
// W15 Cairn — first non-clear objective world. Ships in v0.12.0.
import { WORLD_15_LEVELS } from './world15';
// W16-W19 PARKED for the Android Closed Testing 14-day-window restructure
// (v0.18.0, 2026-05-28). Generated content is intact in the world{16-19}.ts
// wrappers — only the imports + ALL_WORLDS entries below are commented out,
// matching the original W11-W13 parking pattern (see drip-release-playbook).
//
// Drip schedule:
//   v0.18.0 (2026-05-28) — W11-W15 live, W16-W19 parked, paywall enforced,
//                          tester code TESSERA-OPEN active on paywall for
//                          cohort access.
//   v0.18.1 (2026-05-28, same day hotfix) — free worlds (W1-W3) opened
//                          fully (sequential-within-free-world gate
//                          removed in maxLevelFor). World parking
//                          unchanged.
//   v0.18.2 (~day 5, ~2026-06-02) — uncomment W16 + W17 + their entries.
//   v0.19.0 (~day 10, ~2026-06-07) — uncomment W18 + W19 + their entries.
//
// Why this matters: prior testing rounds ran with TEST_MODE_UNLOCK_ALL=true
// on Android, which meant testers never exercised the production paywall
// flow and Google Play's review couldn't see the actual W4+ unlock journey
// that real users would experience. Restructuring to mirror iOS production
// gives Google an authentic engagement signal across the 14-day window
// (testers hit paywall → enter code → progressively gain new content).
//
// v0.18.2 (2026-06-02): W16 Riven + W17 Gale DRIPPED — imports + ALL_WORLDS
// entries below are now live. W18 + W19 remain parked for v0.19.0 (~2026-06-07).
// Do not uncomment W18/W19 without an explicit "execute Update 3" instruction.
import { WORLD_16_LEVELS } from './world16';
import { WORLD_17_LEVELS } from './world17';
// import { WORLD_18_LEVELS } from './world18';
// import { WORLD_19_LEVELS } from './world19';
import { BONUS_LEVELS } from './bonus-generated';

// Pool of pearlescent interstitial puzzles, surfaced variable-ratio after a
// clean clear in the main worlds. Not part of any world — lives outside the
// normal progression. See App.tsx's bonus-trigger logic.
export { BONUS_LEVELS };

export interface WorldDef {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly accent: string;
  readonly levels: readonly LevelDef[];
}

// Paid-tier placeholders. Worlds past whatever has shipped content surface as
// locked tabs in the UI so the paywall pitch has concrete, named future-content
// to point at. Each gets promoted to a real entry as its generator lands.
// W5 (Helix) is the first paid-tier world with real content — five colours,
// wider geometry, d=5–7 progression. W6 (Prism) extends that curve: opens at
// d=6 and climaxes at d=7 on denser r=3 lattices. The original "prismatic
// tiles" promise is deferred — the engine doesn't have splitter tiles yet, so
// the tagline now points at the practical character (dense lattices, longer
// chains) rather than a mechanic that doesn't exist.
// All 10 worlds have real content as of v0.7.0. PAID_PLACEHOLDERS kept as
// an empty array — the filter below retains it for W11+ if we ever ship
// post-launch expansion worlds that should surface as locked tabs before
// their content lands.
const PAID_PLACEHOLDERS: readonly WorldDef[] = [];

const ALL_WORLDS: readonly WorldDef[] = [
  {
    index: 1,
    id: 'threshold',
    name: 'Threshold',
    tagline: 'Learn the slide. Learn the chain.',
    accent: '#5A6876',
    levels: WORLD_1_LEVELS,
  },
  {
    index: 2,
    id: 'ember',
    name: 'Ember',
    tagline: 'Three colours. Richer chains.',
    accent: '#C7613A',
    levels: WORLD_2_LEVELS,
  },
  {
    index: 3,
    id: 'tide',
    name: 'Tide',
    tagline: 'Four colours. Deeper reads.',
    accent: '#3F6F8B',
    levels: WORLD_3_LEVELS,
  },
  {
    index: 4,
    id: 'ashwood',
    name: 'Ashwood',
    tagline: 'The final free world. Think before you slide.',
    accent: '#5E4A3A',
    levels: WORLD_4_LEVELS,
  },
  {
    index: 5,
    id: 'helix',
    name: 'Helix',
    tagline: 'Five colours. Wider grid. The climb begins.',
    accent: '#6A4C93',
    levels: WORLD_5_LEVELS,
  },
  {
    index: 6,
    id: 'prism',
    name: 'Prism',
    tagline: 'Dense lattices. Longer chains.',
    accent: '#D46A92',
    levels: WORLD_6_LEVELS,
  },
  {
    index: 7,
    id: 'aurora',
    name: 'Aurora',
    tagline: 'Long chains. Rolling clears.',
    accent: '#4E8A6A',
    levels: WORLD_7_LEVELS,
  },
  {
    index: 8,
    id: 'halcyon',
    name: 'Halcyon',
    tagline: 'Denser boards. No escape.',
    accent: '#3E7B8A',
    levels: WORLD_8_LEVELS,
  },
  {
    index: 9,
    id: 'quartz',
    name: 'Quartz',
    tagline: 'Six colours. Every cluster counts.',
    accent: '#B07050',
    levels: WORLD_9_LEVELS,
  },
  {
    index: 10,
    id: 'obsidian',
    name: 'Obsidian',
    tagline: 'The wall. Everything learned.',
    accent: '#2E2E33',
    levels: WORLD_10_LEVELS,
  },
  // Post-launch expansion content, dripped 2026-04-30 to Android Closed
  // Testing for tester feedback. Each world introduces a categorically
  // distinct mechanic (locks, denser locks, mixers, gravity).
  {
    index: 11,
    id: 'keep',
    name: 'Keep',
    tagline: 'Locked doors. Matching keys.',
    accent: '#8A7A3E',
    levels: WORLD_11_LEVELS,
  },
  {
    index: 12,
    id: 'vault',
    name: 'Vault',
    tagline: 'Deeper doors. More keys needed.',
    accent: '#6E8EA0',
    levels: WORLD_12_LEVELS,
  },
  {
    index: 13,
    id: 'crucible',
    name: 'Crucible',
    tagline: 'Neutral matter. Claim it. Chain it.',
    accent: '#7A8A9C',
    levels: WORLD_13_LEVELS,
  },
  {
    index: 14,
    id: 'ballast',
    name: 'Ballast',
    tagline: 'The board has weight. Tiles fall along the arrows.',
    accent: '#7A8471',
    levels: WORLD_14_LEVELS,
  },
  {
    index: 15,
    id: 'cairn',
    name: 'Cairn',
    tagline: 'Reach the marked stones.',
    accent: '#7B4F6E',
    levels: WORLD_15_LEVELS,
  },
  // W16 + W17 LIVE as of v0.18.2 (2026-06-02 drip). W18 + W19 remain parked
  // for v0.19.0 (~2026-06-07) — see import-block comment above for schedule.
  {
    index: 16,
    id: 'riven',
    name: 'Riven',
    tagline: 'Some cells are gone.',
    accent: '#6B5239',
    levels: WORLD_16_LEVELS,
  },
  {
    index: 17,
    id: 'gale',
    name: 'Gale',
    tagline: 'One cell further than you meant.',
    accent: '#6E8EA0',
    levels: WORLD_17_LEVELS,
  },
  // {
  //   index: 18,
  //   id: 'embers',
  //   name: 'Embers',
  //   tagline: 'Merge with care. Some tiles burn out.',
  //   accent: '#A5572D',
  //   levels: WORLD_18_LEVELS,
  // },
  // {
  //   index: 19,
  //   id: 'cipher',
  //   name: 'Cipher',
  //   tagline: 'Everything you have learned. One board.',
  //   accent: '#2E2E33',
  //   levels: WORLD_19_LEVELS,
  // },
  ...PAID_PLACEHOLDERS,
];

// Free worlds must have content. Paid placeholders show as locked tabs even
// with empty levels — that's intentional for the paywall pitch.
export const WORLDS: readonly WorldDef[] = ALL_WORLDS.filter(
  (w) => w.levels.length > 0 || w.index > 4,
);

export function worldByIndex(i: number): WorldDef {
  const w = WORLDS.find((w) => w.index === i);
  if (!w) throw new Error(`no world ${i}`);
  return w;
}
